import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { resolveSeo, SITE_NAME, SITE_IMAGE } from '@/seo/seoData';

function upsertMeta(attr: 'name' | 'property', key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Route-aware SEO head manager. On every route change it restores or replaces
 * the managed <title>, description, keywords, canonical, Open Graph, and
 * Twitter Card tags so metadata never leaks between routes.
 */
export function useSeo(): void {
  const location = useLocation();

  useEffect(() => {
    const seo = resolveSeo(location.pathname);

    document.title = seo.title;
    upsertMeta('name', 'description', seo.description);
    upsertMeta('name', 'keywords', seo.keywords);
    upsertCanonical(seo.canonical);

    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:type', seo.ogType);
    upsertMeta('property', 'og:url', seo.canonical);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:image', SITE_IMAGE);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.description);
    upsertMeta('name', 'twitter:image', SITE_IMAGE);
  }, [location.pathname]);
}